/**
 * Agent chat API service.
 * Submit + poll pattern: POST creates a request, GET polls for results.
 */
import { sentryApiRequest } from '../utils/api';
import type { AgentSession, AgentSessionMessage } from '../types';

export type { AgentSession, AgentSessionMessage } from '../types';

export interface AgentEvent {
  type: 'text' | 'tool_start' | 'tool_done' | 'continue';
  content?: string;
  tool?: string;
  summary?: string;
}

export interface PageContext {
  currentPage: string;
  projectId?: string;
  projectName?: string;
  activeTab?: string;
  documentCount?: number;
  columnCount?: number;
  analysisStatus?: string;
  documents?: Array<{ documentId: string; fileName: string; status: string }>;
  columns?: Array<{ columnId: string; name: string; type: string }>;
  analysisRunId?: string;
}

export interface AgentSubmitResponse {
  requestId: string;
}

export interface AgentPollResponse {
  status: string;
  events?: AgentEvent[];
  agentResponse?: string;
  errorMessage?: string;
}

export function submitAgentMessage(
  sessionId: string,
  message: string,
  pageContext: PageContext,
): Promise<AgentSubmitResponse> {
  return sentryApiRequest<AgentSubmitResponse>('/agent/chat', {
    method: 'POST',
    body: JSON.stringify({ sessionId, message, pageContext }),
  });
}

export function pollAgentMessage(requestId: string): Promise<AgentPollResponse> {
  return sentryApiRequest<AgentPollResponse>(`/agent/chat/${requestId}`, {
    method: 'GET',
  });
}

export function fetchSessions(): Promise<AgentSession[]> {
  return sentryApiRequest<{ sessions: AgentSession[] }>('/agent/chat/sessions', {
    method: 'GET',
  }).then(function (data) {
    return data.sessions;
  });
}

export function fetchSessionMessages(sessionId: string): Promise<AgentSessionMessage[]> {
  return sentryApiRequest<{ messages: AgentSessionMessage[] }>(
    `/agent/chat/sessions?sessionId=${encodeURIComponent(sessionId)}`,
    { method: 'GET' },
  ).then(function (data) {
    return data.messages;
  });
}

export function deleteSession(sessionId: string): Promise<void> {
  return sentryApiRequest<any>(
    `/agent/chat/sessions?sessionId=${encodeURIComponent(sessionId)}`,
    { method: 'DELETE' },
  ).then(function () {
    return;
  });
}
